import { storeToRefs } from "pinia";
import { useConditionsStore } from "@stores/survey/conditions/conditions";
import { cardTokenToOption } from "@utils/computeCardOptions";
import { numericConditionSegments } from "@utils/conditionOperators";
import type { Condition, ConditionsGroup } from "@/types";

export interface Segment {
  kind: "q" | "v" | "join" | "plain" | "empty";
  text: string;
}

interface Option {
  value: string | number;
  label: string;
}

const stripCode = (label: string) => String(label).replace(/^\d+\.\s*/, "");

export const useConditionsSentence = () => {
  const conditionsStore = useConditionsStore();
  const { cardTokenOptions } = storeToRefs(conditionsStore);
  const { valuesOptions } = conditionsStore;

  const questionLabel = (condition: Condition): string => {
    const optionValue = cardTokenToOption(condition.card_token, true);
    const options: Option[] = cardTokenOptions.value ?? [];
    return options.find((option) => option.value === optionValue)?.label ?? condition.condition_name ?? "…";
  };

  const valueLabels = (condition: Condition): string[] => {
    const options: Option[] = valuesOptions(condition) ?? [];
    return (condition.values ?? []).map((value) => {
      const option = options.find((o) => String(o.value) === String(value));
      return stripCode(option?.label ?? value);
    });
  };

  const conditionSegments = (condition: Condition): Segment[] => {
    const question: Segment = { kind: "q", text: questionLabel(condition) };
    const numeric: Segment[] | null = numericConditionSegments(condition);
    if (numeric) return [question, ...numeric];

    const segments: Segment[] = [question, { kind: "plain", text: condition.type === "is_not" ? " is not " : " is " }];
    const labels = valueLabels(condition);
    if (!labels.length) return [...segments, { kind: "empty", text: "…" }];
    labels.forEach((label, index) => {
      if (index > 0) segments.push({ kind: "plain", text: index === labels.length - 1 ? " or " : ", " });
      segments.push({ kind: "v", text: label });
    });
    return segments;
  };

  const groupSegments = (conditions: Condition[]): Segment[] => {
    const segments: Segment[] = [];
    conditions.forEach((condition, index) => {
      if (index > 0) segments.push({ kind: "join", text: ` ${conditions[index - 1].operator || "or"} ` });
      segments.push(...conditionSegments(condition));
    });
    return segments;
  };

  const groupsSegments = (groups?: ConditionsGroup[]): Segment[] => {
    const filled = (groups ?? []).filter((group) => group.group_conditions?.length);
    const segments: Segment[] = [];
    filled.forEach((group, index) => {
      if (index > 0) segments.push({ kind: "join", text: ` ${filled[index - 1].group_operator || "or"} ` });
      if (filled.length > 1) segments.push({ kind: "plain", text: "(" });
      segments.push(...groupSegments(group.group_conditions));
      if (filled.length > 1) segments.push({ kind: "plain", text: ")" });
    });
    return segments;
  };

  return { groupsSegments };
};
